import { inject } from '@angular/core';
import { CanActivateFn, Router, Routes } from '@angular/router';

import { privateRouteGuard } from '@guards/private-route.guard';
import { rolePermissionGuard } from '@guards/role.guard';

import { UserPermissionService } from '@services/helper/user-permission.service';

/**
 * Where `/settings` lands. Permissions for everyone who can edit it (QA 12 Aug),
 * Delete History for Developer Viewer — a plain `redirectTo: 'permissions'`
 * would bounce that role straight back out through `rolePermissionGuard`.
 */
export const settingsLandingGuard: CanActivateFn = () => {
  const router = inject(Router);
  const canManagePermissions = !inject(UserPermissionService).isUserDeveloperViewer();

  return router.createUrlTree([
    '/settings',
    canManagePermissions ? 'permissions' : 'delete-history',
  ]);
};

/**
 * Settings feature routes (FEC-1160 / FEC-1174).
 *
 * `/settings` is a shell with a left sub-menu and four children. Developer
 * Viewer reaches the read-only tabs but not the permissions matrix, whose only
 * control writes.
 *
 * QA 12 Aug (Hina): opening Settings must land on Permissions, and the screen is
 * called "Permissions" rather than "Roles and Permissions" — hence both the
 * default child and the segment. `settingsLandingGuard` sends the one role that
 * cannot open it (Developer Viewer) to Delete History instead of a dead end.
 *
 * `/permissions` (the FEC-759 path, still in bookmarks) redirects here.
 */
export const SETTINGS_ROUTES: Routes = [
  {
    path: 'settings',
    loadComponent: () =>
      import('@pages/settings/settings.component').then((c) => c.SettingsComponent),
    data: { layout: 'private', roles: ['developer', 'developer viewer', 'master agent'] },
    canActivate: [privateRouteGuard, rolePermissionGuard],
    canActivateChild: [privateRouteGuard],
    children: [
      { path: '', pathMatch: 'full', canActivate: [settingsLandingGuard], children: [] },
      {
        path: 'permissions',
        loadComponent: () =>
          import('@pages/settings/roles-permissions/roles-permissions.component').then(
            (c) => c.RolesPermissionsComponent,
          ),
        data: { layout: 'private', roles: ['developer', 'master agent'] },
        canActivate: [rolePermissionGuard],
      },
      {
        path: 'delete-history',
        loadComponent: () =>
          import('@pages/settings/delete-history/delete-history.component').then(
            (c) => c.DeleteHistoryComponent,
          ),
        data: { layout: 'private' },
      },
      {
        path: 'import-history',
        loadComponent: () =>
          import('@pages/settings/import-history/import-history.component').then(
            (c) => c.ImportHistoryComponent,
          ),
        data: { layout: 'private' },
      },
      {
        path: 'general',
        loadComponent: () =>
          import('@pages/settings/general-settings/general-settings.component').then(
            (c) => c.GeneralSettingsComponent,
          ),
        data: { layout: 'private' },
      },
    ],
  },
  {
    // FEC-759 shipped the matrix at /permissions; it now lives under Settings.
    path: 'permissions',
    redirectTo: 'settings/permissions',
    pathMatch: 'full',
  },
];
